// The Maw's line pools (docs/06 §6). Text lives in i18n: display lines are `maw.<trigger>.<n>`,
// Act I receipt-printer lines are `receipt.<trigger>.<n>` (1-based).
import type { Expression } from '../sim/types';

export interface DialoguePool {
  /** Number of display lines (after It Wakes). */
  count: number;
  /** Number of receipt-printer lines (Act I, before the face exists). */
  receipt?: number;
  /** Common triggers stay silent 30% of the time. */
  common?: boolean;
  expression?: Expression;
}

export const DIALOGUE = {
  firstSpin: { count: 3, expression: 'pleased' },
  dead: { count: 6, common: true, expression: 'neutral' },
  small: { count: 6, common: true, expression: 'pleased' },
  big: { count: 5, receipt: 1, expression: 'pleased' },
  epic: { count: 5, receipt: 1, expression: 'laughing' },
  gorge: { count: 3, expression: 'devouring' },
  nearMiss: { count: 4, common: true, expression: 'suspicious' },
  nudge: { count: 4, common: true, expression: 'neutral' },
  sulk: { count: 3, expression: 'sulking' },
  hold: { count: 3, common: true, expression: 'suspicious' },
  payEarly: { count: 3, receipt: 1, expression: 'pleased' },
  barelyPaid: { count: 2, receipt: 1, expression: 'suspicious' },
  paid: { count: 4, receipt: 2, expression: 'pleased' },
  consumed: { count: 4, receipt: 1, expression: 'devouring' },
  offer: { count: 2, receipt: 1, expression: 'pleased' },
  draft: { count: 3, common: true, expression: 'neutral' },
  shop: { count: 3, common: true, expression: 'neutral' },
  bonus: { count: 3, receipt: 1, expression: 'surprised' },
  bonusVoid: { count: 2, receipt: 1, expression: 'laughing' },
  windowStart: { count: 4, receipt: 1, common: true, expression: 'neutral' },
  lastSpin: { count: 3, common: true, expression: 'hungry' },
  hungry: { count: 0, receipt: 1 },
  wake: { count: 3, expression: 'surprised' },
  peckish: { count: 3, expression: 'hungry' },
  ravenous: { count: 3, expression: 'ravenous' },
  starving: { count: 3, expression: 'starving' },
  eat: { count: 4, expression: 'devouring' },
  fed: { count: 4, common: true, expression: 'pleased' },
  adoring: { count: 2, expression: 'adoring' },
  // ── Act II ──
  associate: { count: 3, expression: 'suspicious' },
  reckoning: { count: 3, expression: 'suspicious' },
  reckoningWin: { count: 4, expression: 'sulking' },
  twins: { count: 2, expression: 'suspicious' },
  hex: { count: 4, expression: 'pleased' },
  cleanse: { count: 2, expression: 'surprised' },
  dealOffer: { count: 4, expression: 'pleased' },
  dealAccept: { count: 4, expression: 'laughing' },
  dealRefuse: { count: 4, expression: 'suspicious' },
  dealRefuseHabit: { count: 3, expression: 'sulking' },
  loanRefused: { count: 1, expression: 'suspicious' },
  covenant: { count: 2, expression: 'adoring' },
  grow: { count: 4, expression: 'surprised' },
  hilo: { count: 3, expression: 'pleased' },
  hiloUp: { count: 4, common: true, expression: 'surprised' },
  hiloDevoured: { count: 3, expression: 'devouring' },
  hiloCollect: { count: 3, expression: 'sulking' },
  gambleOffer: { count: 3, expression: 'pleased' },
  wheel: { count: 3, expression: 'laughing' },
  stepIn: { count: 3, expression: 'surprised' },
  nightShift: { count: 3, expression: 'sleeping' },
  molt: { count: 3, expression: 'surprised' },
  lastMeal: { count: 2, expression: 'suspicious' },
  hand: { count: 3, expression: 'pleased' },
  page: { count: 3, expression: 'suspicious' },
  // ── Acts III–IV ──
  noticed: { count: 3, expression: 'noticed' },
  devour: { count: 3, expression: 'devouring' },
  miracle: { count: 2, expression: 'surprised' },
  doomHigh: { count: 3, expression: 'glitch' },
  plinko: { count: 3, expression: 'laughing' },
  plinkoChomp: { count: 2, expression: 'devouring' },
  feast: { count: 3, expression: 'pleased' },
  feastFull: { count: 2, expression: 'laughing' },
  plug: { count: 3, expression: 'suspicious' },
  endless: { count: 3, expression: 'ravenous' },
} satisfies Record<string, DialoguePool>;

/** Scripted two-line speeches (collector intros, story beats): `maw.<script>.<n>` in order. */
export const SCRIPTS = {
  loan: 2,
  covenantOffer: 2,
  'collector.auditor': 2,
  'collector.glutton': 2,
  'collector.widow': 2,
  'collector.twins': 2,
  'collector.taxman': 2,
  'collector.mirrorman': 2,
  'collector.mothqueen': 2,
  'collector.stopwatch': 2,
  'collector.lastsupper': 2,
  molt: 3,
  siblings: 2,
  cathedral: 3,
  lastSupperPaid: 2,
  sated: 3,
  becomeMaw: 2,
  unplugged: 3,
  hungerStrike: 2,
  idle: 2,
  asleep: 2,
} satisfies Record<string, number>;
export type ScriptId = keyof typeof SCRIPTS;

export function scriptKeys(id: ScriptId): string[] {
  return Array.from({ length: SCRIPTS[id] }, (_, i) => `maw.${id}.${i + 1}`);
}

export type DialogueTrigger = keyof typeof DIALOGUE;

export function dialogueKeys(trigger: DialogueTrigger, channel: 'display' | 'receipt'): string[] {
  const pool: DialoguePool = DIALOGUE[trigger];
  const n = channel === 'display' ? pool.count : (pool.receipt ?? 0);
  const prefix = channel === 'display' ? 'maw' : 'receipt';
  return Array.from({ length: n }, (_, i) => `${prefix}.${trigger}.${i + 1}`);
}
